// ==============================================================================================
//  APPLICATION AUTHORSHIP & RIGHTS NOTICE
//
//  Developed by: Gareth Morgan
//  Brand & Product: BuildMyChecks.co.uk
//  Ownership: Sole author and rights holder
//
//  ----------------------------------------------------------------------------------------------
//  NOTICE:
//  - This application and its related systems were designed, developed, and built solely
//    by Gareth Morgan.
//  - Gareth Morgan retains full authorship, intellectual property, and creative rights to
//    all Build My Checks branding, technology, source code, architecture, and design.
//  - No ownership, license, or implied rights are granted to any third party unless
//    explicitly agreed in writing by Gareth Morgan.
//  - Gareth Morgan is NOT responsible for future maintenance, updates, security patching,
//    or operational upkeep if this application is deployed, copied, or modified without
//    his written consent.
//  - Gareth Morgan reserves the right to:
//        Withdraw or disable access to the application or related services
//        Remove hosted, cloud-based, or linked services under his control
//        Revoke access to Build My Checksrelated assets, code, APIs, or integrations
//
//   2025 Gareth Morgan  BuildMyChecks.co.uk
//  Unauthorized use, alteration, copying, or redistribution without written consent
//  is strictly prohibited.
//
//  ----------------------------------------------------------------------------------------------
//  Build My Checks  ASCII Logo Signature
//  (Symbolic representation of BuildMyChecks.co.uk identity)
//
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................:-==++-.........:-:.............................................
// ...................................-*******:.......=*****+-.........................................
// ...................................=*******+:.....+*******=.........................................
// .........................::........+**********************=.........................................
// .......................-+***+-.:-++***********************=:.......:................................
// .....................-+*************************************+-..=+**+-..............................
// .....................:+*************+=-.............-++***************+:............................
// ......................:+********+=:.....................:=*************=............................
// .......................=******+:...........................-+********+-.............................
// ......................-*****+:...............................-+*****+-..............................
// ................:::::+****+-...................................=*****+..............................
// ...............+-......:=+-........+=.*%=......##:.:::.....:::..-*****=.............................
// ..............=*-*@%%%%@*.........-#*:#@=......@%::@@@*...*@@#...-+++++**++=-.......................
// .............:**-*@*..=@%.=#+..*#=-%#:#@=.=#%#+@%::@%%%-.=@%@#-##:..##:+*****:......................
// ..............-+-*@@%%@%+.+@*..#@==%#:#@==@%-:+@%::@#-@#:%#+%#.+@#.*@+-******=......................
// .................*@*..:#@++@*..#@==%#:#@=+@#...@%::@#.+@%%-=%#..%@#%#:+******+......................
// .................*@%##%@#.-%@%%@@==%#:#@=:#@%#%%%::@#.:#@*.=%#...%@%:=****+-:.......................
// ..................::........::.........:....::.....:....:......:-#@--***+:..........................
// ................:++:+@@@@@*.:@#:.....................#%=......:#%#-.+***+:..........................
// .............-+**+:#@*:.:+%+:@#-=+-...:=+=-....-+=-..#%=.:=-..-++-..+***+:..........................
// .............+***=-@%.......:@@#+#@#.+@#-+%#:+@%++@%:#%+*@#::%#--*-:*****+-.........................
// .............-***=:@%.......:@%:.-%%:%@%%%%%+#@=.....#@@@%:..#%@@%-=**+++++*+.......................
// ..............****-+%@*=+%@*:@%:.-%%.*@*-=*=.*@%==%#:#@+=@%=:++-+@*-+--===:+=.......................
// .................::::=***=:..++..:++..-+**=...:+**+:.=+-.:++:-*#*=::-:+***=::-++:...................
// ....................-****+=............................*#......+*****************=..................
// .....................=*****=.......+##+.:+##=....*+.-#-*#.**:..:+************++*=...................
// .....................+******+-....*%..:.%*.:%+..:%*.=%=*%%*..:--***********+:..-+:..................
// ....................+*********=:%=+%*+%-*%+*%-=#-#%*%@=*#:%*:+****+++****+=...=*****-...............
// ...................=**********+--::.::....::...:..::.:.:-::-:=***+:..=+*+:..:+******-...............
// ....................-******++*******+=-:............-=++****+=+***+:...:...=******+-:...............
// ......................-++-....=+***************************+=+-=****+:...-+******-..................
// ................................=**********************++-:....=******=:+********=..................
// ................................=**********************=......:+*****************+:.................
// ................................+*******=......+*******=........-+=.:=+****+-.-+-...................
// ................................:-=+***-........+******=..............=***+.........................
// .................................................--::...............................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
//
// ==============================================================================================

import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import ProgressStepper from '../../components/ProgressStepper';

const REGISTRATION_STEPS = [
  { label: 'Create Account' },
  { label: 'Company Setup' },
  { label: 'Start Trial' },
];

export default function TrialSuccess() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userCount, companyId } = location.state || {};
  const [hasCompanyAccess, setHasCompanyAccess] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Wait for the user's companyId to be updated in their profile
    if (!auth.currentUser) {
      navigate('/login');
      return;
    }

    const userDocRef = doc(db, 'users', auth.currentUser.uid);
    const unsubscribe = onSnapshot(userDocRef, (userDoc) => {
      if (userDoc.exists()) {
        const userData = userDoc.data();
        if (userData.companyId) {
          setHasCompanyAccess(true);
          setLoading(false);
        }
      }
    });

    // Timeout after 10 seconds in case something goes wrong
    const timeout = setTimeout(() => {
      setLoading(false);
      setHasCompanyAccess(true);
    }, 10000);

    return () => {
      unsubscribe();
      clearTimeout(timeout);
    };
  }, [navigate]);

  const handleContinueToApp = () => {
    if (hasCompanyAccess) {
      // Redirect to the main home page
      navigate('/home');
    }
  };

  const handleUpgradeNow = () => {
    navigate('/checkout', { state: { userCount, companyId } });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-gray-200 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="flex justify-center mb-6">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-green-500"></div>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Setting up your account...</h2>
          <p className="text-gray-600">
            Please wait while we finalize your trial setup.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-gray-200 flex flex-col items-center justify-center p-4">
      <div className="mb-2">
        <ProgressStepper steps={REGISTRATION_STEPS} currentStep={2} variant="light" />
      </div>
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
        <div className="flex justify-center mb-6">
          <CheckCircle className="h-16 w-16 text-green-500" />
        </div>

        <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome to BuildMyChecks!</h1>
        <p className="text-gray-600 mb-6">
          Your 14-day free trial has started. You can now access all features without any payment required.
        </p>

        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-green-800 mb-2">Your Trial Includes:</h3>
          <ul className="text-sm text-green-700 text-left space-y-1">
            <li>• {userCount || 1} user account{userCount > 1 ? 's' : ''}</li>
            <li>• Unlimited form creation</li>
            <li>• Digital signatures</li>
            <li>• Photo attachments</li>
            <li>• PDF reports</li>
            <li>• Full access to all features</li>
          </ul>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-blue-800">
            <strong>Important:</strong> Your trial ends in 14 days. After that, you'll need to upgrade to continue using the service.
          </p>
        </div>

        <div className="space-y-3">
          <button
            onClick={handleContinueToApp}
            disabled={!hasCompanyAccess}
            className="w-full bg-primary hover:bg-primary-dark text-white font-semibold py-3 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Start Using BuildMyChecks
          </button>

          <button
            onClick={() => navigate('/checkout', { state: { userCount, companyId } })}
            className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 px-6 rounded-lg transition-colors"
          >
            Upgrade Now (£{((userCount || 1) * 5).toFixed(2)}/month)
          </button>
        </div>

        <p className="text-xs text-gray-500 mt-4">
          You can upgrade anytime during your trial. No charges until trial ends.
        </p>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------------------------
//  Build My Checks (c) 2025 Gareth Morgan - BuildMyChecks.co.uk
//  Solely designed, developed, and maintained by Gareth Morgan.
//  All intellectual property rights reserved.
//  No liability is accepted for unauthorised use, modification, deployment, or maintenance.
// ----------------------------------------------------------------------------------------------
